import { Check, Sparkle, X } from "@phosphor-icons/react";

const slug = (v) => String(v).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const norm = (o) => (Array.isArray(o) ? { value: o[0], label: o[1], color: o[2] } : typeof o === "string" ? { value: o, label: o } : o);

export function Q({ label, hint, children, className = "" }) {
  return (
    <div className={`mt-6 first:mt-0 ${className}`}>
      <p className="text-sm font-medium">{label}</p>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
      <div className="mt-2.5">{children}</div>
    </div>
  );
}

export default function ChipGroup({ options, value, onChange, multi = false, prefix, large = false, allowClear = true, marked = [], onRemove, removable }) {
  const items = options.map(norm);
  const has = (v) => (multi ? (value || []).includes(v) : value === v);
  const pick = (v) => {
    if (multi) onChange(has(v) ? value.filter((x) => x !== v) : [...(value || []), v]);
    else onChange(has(v) && allowClear ? "" : v);
  };
  return (
    <div className={large ? "grid grid-cols-2 sm:grid-cols-3 gap-2" : "flex flex-wrap gap-2"} data-testid={`${prefix}-group`} role={multi ? "group" : "radiogroup"}>
      {items.map(({ value: v, label, color, icon: Icon }) => {
        const on = has(v);
        const canRemove = onRemove && (!removable || removable.includes(v));
        return (
          <button key={v} type="button" onClick={() => pick(v)} aria-pressed={on} data-testid={`${prefix}-${slug(v)}`}
            className={`${large ? "min-h-14 py-2 px-4 max-sm:px-3 rounded-2xl justify-start text-sm" : "h-9 coarse:h-10 px-3.5 rounded-full text-[13px]"} inline-flex items-center gap-2 font-medium transition-[background-color,color,box-shadow,transform] duration-150 active:scale-[0.97] ${
              on ? "bg-foreground text-background shadow-[0_4px_12px_-6px_rgba(0,0,0,0.4)]" : "bg-muted/70 text-foreground/75 hover:bg-muted hover:text-foreground"}`}>
            {Icon && <Icon weight={on ? "fill" : "duotone"} className={large ? "h-5 w-5 shrink-0" : "h-4 w-4 shrink-0"} />}
            {!Icon && color && <span className="h-2 w-2 rounded-full shrink-0" style={{ background: color }} />}
            <span className={large ? "min-w-0 text-left leading-tight break-words" : "truncate"}>{label}</span>
            {marked.includes(v) ? <Sparkle weight="fill" data-testid={`${prefix}-ai-${slug(v)}`} className={`h-3 w-3 -mr-0.5 shrink-0 ${on ? "text-background/80" : "text-primary"} ${large ? "ml-auto" : ""}`} />
              : multi && on && !large && <Check weight="bold" className="h-3 w-3 -mr-0.5" />}
            {canRemove && (
              <span role="button" tabIndex={0} aria-label={`Remove ${label} from suggestions`} title="Remove from suggestions" data-testid={`${prefix}-remove-${slug(v)}`}
                onClick={(e) => { e.stopPropagation(); onRemove(v); }} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onRemove(v); } }}
                className={`-mr-1.5 ml-0.5 h-5 w-5 shrink-0 rounded-full inline-flex items-center justify-center transition-colors ${on ? "text-background/70 hover:text-background hover:bg-background/15" : "text-muted-foreground/70 hover:text-foreground hover:bg-foreground/10"}`}>
                <X weight="bold" className="h-2.5 w-2.5" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
